import { Injectable } from '@angular/core';
import {HttpBackend, HttpClient, HttpParams} from '@angular/common/http';
import {AuthService} from './auth.service';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {CommunityMember} from './CommunityMember';
import {CommunityMessagePostElement} from './CommunityMessagePostElement';
import {CommunityMessageResponseElement} from './community-message-response-element';
import {DesignInvitation} from './DesignInvitation';

@Injectable({
  providedIn: 'root'
})
export class CommunityConnectorService {

  private readonly COMMUNITY_BASE_URL = '/community';
  private readonly COMMUNITY_EXTERN_URL = 'http://community.empamos.de';
  private httpClient: HttpClient;

  constructor(private handler: HttpBackend, private authService: AuthService) {
    this.httpClient = new HttpClient(handler);
  }

  public getAllUsers(): Observable<CommunityMember[]> {
    return this.authService.getUserId$().pipe(
      switchMap(userid => {
        const params = new HttpParams().set('senderAppId', userid);
        return this.httpClient.get<CommunityMember[]>(`${this.COMMUNITY_BASE_URL}/getAllUsers`, {params});
      })
    );
  }

  public checkUserByMail(): Observable<CommunityMember[]> {
    return this.authService.getUserMail$().pipe(
      switchMap(email => {
        const name = email.split('@')[0];
        const domain = email.split('@')[1];
        const params = new HttpParams().set('name', name).set('domain', domain);
        return this.httpClient.get<CommunityMember[]>(`${this.COMMUNITY_BASE_URL}/checkUserByMail`, {params});
      })
    );
  }

  public createDesignInvitation(
    senderUsername: string,
    senderId: number,
    title: string,
    message: string,
    receiverUsername: string,
    receiverId: number,
    designName: string,
    shareOption: string,
    timestamp: string): Observable<CommunityMessageResponseElement>
  {
    let communityMessagePostElement: CommunityMessagePostElement;
    communityMessagePostElement = {
      senderUsername,
      senderId,
      title, message,
      receiverUsername,
      receiverId,
      designName,
      shareOption,
      timestamp};
    return this.authService.getUserId$().pipe(
      switchMap(userid => {
          const params = new HttpParams().set('senderAppId', userid);
          return this.httpClient.post<CommunityMessageResponseElement>(
              `${this.COMMUNITY_BASE_URL}/createDesignInvitation`, communityMessagePostElement, {params});
      })
    );
  }

  getDesignInvitation(timestamp: string, receiverId: number) {
      return this.authService.getUserId$().pipe(
          switchMap(userid => {
              const params = new HttpParams()
                  .set('userAppId', userid)
                  .set('timestamp', timestamp)
                  .set('receiverId', receiverId);
              return this.httpClient.get<DesignInvitation>(`${this.COMMUNITY_BASE_URL}/getDesignInvitation`, {params});
          })
      );
  }

  getCommunityMemberUrl(username: string) {
    return this.COMMUNITY_EXTERN_URL + '/u/' + username + '/summary';
  }

  getCommunityMessageUrl(postUrl: string) {
      return this.COMMUNITY_EXTERN_URL + postUrl;
  }

  getAllDesignInvitations(designName: string): Observable<DesignInvitation[]> {
    return this.authService.getUserId$().pipe(
      switchMap(userid => {
        const params = new HttpParams()
          .set('senderAppId', userid)
          .set('designName', designName);
        return this.httpClient.get<DesignInvitation[]>(`${this.COMMUNITY_BASE_URL}/getAllDesignInvitations`, {params});
      })
    );
  }

  deleteDesignInvitation(designInvitation: DesignInvitation) {
    return this.authService.getUserId$().pipe(
      switchMap(userid => {
        const params = new HttpParams().set('userAppId', userid);
        return this.httpClient.post<DesignInvitation>(
          `${this.COMMUNITY_BASE_URL}/deleteDesignInvitation`, designInvitation, {params});
      })
    );
  }

  acceptDesignInvitation(designInvitation: DesignInvitation) {
    return this.authService.getUserId$().pipe(
      switchMap(userid => {
        const params = new HttpParams().set('receiverAppId', userid);
        return this.httpClient.put<CommunityMessageResponseElement>(
          `${this.COMMUNITY_BASE_URL}/acceptDesignInvitation`, designInvitation, {params});
      })
    );
  }

  getAvatarByUserId(discourseUserId: number) {
    return this.authService.getUserId$().pipe(
      switchMap(userid => {
        const params = new HttpParams()
          .set('userAppId', userid)
          .set('userId', discourseUserId);
        return this.httpClient.get<CommunityMember>(`${this.COMMUNITY_BASE_URL}/getUserAvatar`, {params});
      })
    );
  }

  getAvatarTemplateUrl(avatarTemplate: string) {
    return this.COMMUNITY_EXTERN_URL + avatarTemplate;
  }
}
